import { FirebaseError } from 'firebase/app'
import { auth, functions } from '@utils/firebase'
import firebase from 'firebase/compat/app'
import { ApiResponse } from '@Interfaces/Api/ResponsesInterface'
import type { AuthErrorCode } from '@Interfaces/Api/ErrorsInterface'

/**
 * Sign up with email (for linking to existing account)
 */
export const signUpWithEmail = async (
  email: string,
  password: string
): Promise<ApiResponse<boolean>> => {
  try {
    if (!auth.currentUser) {
      return {
        success: false,
        error: 'No current user to link email to',
        data: undefined,
      }
    }

    const credential = firebase.auth.EmailAuthProvider.credential(email, password)

    await auth.currentUser.linkWithCredential(credential)

    return {
      success: true,
      data: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to link email',
      data: undefined,
    }
  }
}

/**
 * Sign up with email and password
 */
export const signUpWithEmailAndPassword = async (
  email: string,
  password: string
): Promise<ApiResponse<boolean>> => {
  try {
    await auth.createUserWithEmailAndPassword(email, password)

    return {
      success: true,
      data: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to create account',
      data: undefined,
    }
  }
}

/**
 * Login with email and password
 */
export const login = async (
  email: string,
  password: string
): Promise<ApiResponse<{ emailError?: string; passwordError?: string }>> => {
  try {
    await auth.signInWithEmailAndPassword(email, password)

    return {
      success: true,
      data: {},
    }
  } catch (error: unknown) {
    const errorCode: AuthErrorCode | undefined =
      error instanceof FirebaseError ? error.code : undefined
    if (errorCode === 'auth/invalid-email') {
      return {
        success: false,
        error: 'Invalid email address',
        data: { emailError: 'Invalid email address' },
      }
    } else {
      return {
        success: false,
        error: 'Invalid password',
        data: { passwordError: 'Invalid password' },
      }
    }
  }
}

/**
 * Logout
 */
export const logout = async (): Promise<ApiResponse<boolean>> => {
  try {
    await auth.signOut()

    return {
      success: true,
      data: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to logout',
      data: undefined,
    }
  }
}

/**
 * Send password reset email
 */
export const sendPasswordReset = async (email: string): Promise<ApiResponse<boolean>> => {
  try {
    await auth.sendPasswordResetEmail(email)

    return {
      success: true,
      data: true,
      message: 'Password reset email successfully sent',
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to send password reset email',
      data: undefined,
    }
  }
}

/**
 * Check email sign in methods
 */
export async function checkEmailSignInMethods(
  email: string
): Promise<ApiResponse<{ exists: boolean }>> {
  try {
    if (!email) {
      return {
        success: true,
        data: { exists: false },
      }
    }

    const userEmailExists = await auth.fetchSignInMethodsForEmail(email)

    return {
      success: true,
      data: { exists: userEmailExists?.length > 0 },
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to check email',
      data: undefined,
    }
  }
}

/**
 * Check phone sign in methods
 */
export async function checkPhoneSignInMethods(
  phoneNumber: string
): Promise<ApiResponse<{ exists: boolean }>> {
  try {
    const checkPhoneExists = await functions.httpsCallable('checkPhoneExistsV2')({
      phoneNumber,
    })

    const { data } = checkPhoneExists
    const { exists } = data

    return {
      success: true,
      data: { exists },
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to check phone number',
      data: undefined,
    }
  }
}

/**
 * Sign in with phone number
 * Note: This requires UI interaction for verification code
 */
export async function signInWithPhoneNumber(
  phoneNumber: string
): Promise<ApiResponse<{ confirmation: any }>> {
  try {
    const applicationVerifier = new firebase.auth.RecaptchaVerifier('recaptcha-container')

    const confirmationResult = await auth.signInWithPhoneNumber(phoneNumber, applicationVerifier)

    return {
      success: true,
      data: { confirmation: confirmationResult },
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to sign in with phone number',
      data: undefined,
    }
  }
}
